/**
 * aXai AI avatar video service: offers, samples and launch prices (AUD, inc GST).
 * Prices approved by Annette 30 Sep 2026 (AXAI-SPEC-018 §4.1).
 * Public copy: sell the outcome and the avatar presenter; never describe how the work is produced.
 */

import {
  AI_AND_SOFTWARE_SYSTEMS_TRANSCRIPT,
  AI_FOR_NDIS_TRANSCRIPT,
  AI_SUPPORTED_WORK_TRANSCRIPT,
  AXEL_HAS_A_TURN_TRANSCRIPT,
  NEVER_FUNDED_TRANSCRIPT,
  ONE_MESSAGE_TRANSCRIPT,
  THE_ARITHMETIC_TRANSCRIPT,
  WHAT_AXAI_DOES_TRANSCRIPT,
} from './video-transcripts';
import { AI_SUPPORTED_WORK_PATH } from '@/lib/cta-destinations';

export interface VideoOffer {
  name: string;
  scope: string;
  price: string;
}

/**
 * §4A R-32f: the ONE "from" price every page quotes for videos (the lowest offer). Pages never
 * hard-code a price; they import this or VIDEO_OFFERS.
 */
export const VIDEO_FROM_PRICE = '$149';

export const VIDEO_OFFERS: VideoOffer[] = [
  { name: 'Social video', scope: 'Up to 90 seconds. One clear message for social media or your website.', price: VIDEO_FROM_PRICE },
  { name: 'Explainer video', scope: 'Up to 4 minutes. Explain a service, a process or a policy.', price: '$199' },
  { name: 'Training video', scope: 'Up to 8 minutes. Staff induction or training, in up to seven scenes.', price: '$275' },
  { name: 'Five social videos', scope: 'A set of five short videos for one campaign.', price: '$595' },
  { name: 'Five explainer videos', scope: 'A set of five videos up to 4 minutes each.', price: '$895' },
  { name: 'Training programs', scope: 'Ten or more videos, such as a full induction series.', price: 'Fixed quote' },
];

export const VIDEO_INCLUDED = [
  'An AI avatar presenter chosen with you',
  'Script written for you from your material',
  'Your logo, colours and fonts throughout',
  'Captions on every video',
  'Formats for phone and widescreen',
  'A round of changes before you sign off',
];

/**
 * Sample videos shown on /videos. Add a YouTube video id when a sample is approved for
 * public use (aXai-owned demos, or client work with written permission only).
 * The section is hidden until a slot has an id; entries without an id are not rendered as frames.
 */
export interface VideoSample {
  title: string;
  description: string;
  youtubeId?: string;
  /** Poster image served from this site (public/), shown before Play. */
  poster?: string;
  /** YouTube upload date (YYYY-MM-DD) for the VideoObject JSON-LD. */
  uploadDate?: string;
  /** Accessible name for the player iframe; defaults to "<title> sample video". */
  playerTitle?: string;
  /** Verbatim transcript, one paragraph per entry (shown under the player in a <details>). */
  transcript?: string[];
  /** Optional heading for the transcript disclosure; defaults to "Read the transcript". */
  transcriptTitle?: string;
  /** Short label shown above the title (e.g. "Special Edition"), as published on YouTube. */
  edition?: string;
  /** AXAI-VIDEO-002: the one-line caption Annette wrote for each video, shown under the player. */
  captionLine?: string;
}



export const VIDEO_SAMPLES: VideoSample[] = [
  // WITHDRAWN CONTENT IS NOT PUBLISHED HERE. "Never Funded" (V1) and "Axel Has a Turn" (SE01) were
  // the first two samples and are gone, because the scripts they were rendered from are withdrawn.
  // Scripts v0.4, approved by Annette 9 Oct 2026 ("yes - all approved"), removed "the NDIS, the
  // cuts, institutions, government, human rights, ministers and taxpayers" and retitled V1 "Open
  // for Business". V1 needed a heavy rewrite and SE01 scenes 2 to 4; neither has been re-rendered
  // (the re-render decision is still open in the v0.4 change summary, and the Videos folder holds
  // only the 1 Oct files), and YouTube cannot swap the file on an existing video - so the only way
  // to stop the site publishing the withdrawn words was to take the two samples off it.
  //
  // Annette, 21 Oct 2026: "that satirical video had been replaced by something else on the live
  // site ... somehow the content has been reverted too". The repo never carried v0.4 at all: it
  // held the v0.3 scripts, the v0.3 transcripts, and a test pinning V1 to v0.3. v0.4 is now in
  // docs/videos/ beside them.
  //
  // The five service videos below (V3 to V7) are the round that followed v0.4 and are clean -
  // checked line by line against the terms v0.4 removed.
  //
  // PUT THEM BACK when the v0.4 renders exist: "Open for Business" for V1, SE01 scenes 2 to 4, new
  // YouTube ids, and transcripts verbatim from v0.4. tests/unit/video-transcript.test.tsx fails if
  // any sample carries a withdrawn term.
  {
    title: 'This Is AI-Supported Work',
    description: 'Axel explains AI-supported work: real tasks, clear standards and a person checking, with AI doing the part between him and the keyboard.',
    youtubeId: 'H2uC3ns_qfU',
    poster: '/videos/this-is-ai-supported-work-thumbnail.jpg',
    uploadDate: '2026-10-03',
    playerTitle: 'This Is AI-Supported Work — video presented by Axel\'s AI avatar',
    transcript: AI_SUPPORTED_WORK_TRANSCRIPT,
  },
  {
    title: 'The Arithmetic',
    description:
      "Axel sets out the price of a social video, up to 90 seconds, at $149, and compares it with five visits to Metung Hot Springs ($148). Captions, a transcript and an Easy Read version are included in that price.",
    youtubeId: 'y3TMH_1pPDI',
    poster: '/videos/the-arithmetic-thumbnail.jpg',
    uploadDate: '2026-10-10',
    playerTitle: 'The Arithmetic — video presented by Axel\'s AI avatar',
    captionLine: '$149 per social video. $148 per five Wednesdays. One dollar under review.',
    transcript: THE_ARITHMETIC_TRANSCRIPT,
  },
  {
    title: 'What aXai Does',
    description:
      "Axel lists aXai's services: AI systems and software, accessible websites and documents, prototypes, AI website guides, AI presenter videos, and teaching people with disability to use AI as assistive technology. He describes his own role as checking that a person can use what is built.",
    youtubeId: 'IlLVyXkA12E',
    poster: '/videos/what-axai-does-thumbnail.jpg',
    uploadDate: '2026-10-10',
    playerTitle: 'What aXai Does — video presented by Axel\'s AI avatar',
    captionLine: 'Phase one includes the user.',
    transcript: WHAT_AXAI_DOES_TRANSCRIPT,
  },
  {
    title: 'One Message, More Ways to Understand It',
    description:
      'aXai turns one message into six formats: video, captions, transcript, plain-language version, short summary, and Easy Read with pictures. Each set ends with a short comprehension check.',
    youtubeId: 'rg8vhExlm8k',
    poster: '/videos/one-message-thumbnail.jpg',
    uploadDate: '2026-10-10',
    playerTitle: 'One Message, More Ways to Understand It — video presented by Axel\'s AI avatar',
    captionLine: 'Received and understood. In that order.',
    transcript: ONE_MESSAGE_TRANSCRIPT,
  },
  {
    title: 'AI for NDIS and Disability Providers',
    description:
      "Staff ask the assistant a question. It answers only from the provider's approved documents, cites the page it used, and refers the question to a person when the answer is not in the documents.",
    youtubeId: 'KyrXroOkZ7U',
    poster: '/videos/ai-for-ndis-thumbnail.jpg',
    uploadDate: '2026-10-10',
    playerTitle: 'AI for NDIS and Disability Providers — video presented by Axel\'s AI avatar',
    captionLine: 'Source: page eleven.',
    transcript: AI_FOR_NDIS_TRANSCRIPT,
  },
  {
    title: 'AI and Software Systems',
    description:
      'aXai builds software around a decision log. Every step is planned before work starts, every decision is recorded with a name and a time, and a person signs off before any change.',
    youtubeId: 'NBE1X-CxIFI',
    poster: '/videos/ai-and-software-systems-thumbnail.jpg',
    uploadDate: '2026-10-10',
    playerTitle: 'AI and Software Systems — video presented by Axel\'s AI avatar',
    captionLine: 'Line forty-two, signed and dated.',
    transcript: AI_AND_SOFTWARE_SYSTEMS_TRANSCRIPT,
  },
  { title: 'Staff induction', description: 'A welcome video for new team members.' },
  { title: 'Service explainer', description: 'Who you help, how it works and how to get started.' },
  { title: 'Social video', description: 'A short, shareable video for one clear message.' },
];

/**
 * /videos shows the samples section and its "Watch samples" button only when at least one sample
 * has a YouTube id; until then the page promises nothing it cannot show.
 */
export const hasPublishedSamples = (samples: VideoSample[] = VIDEO_SAMPLES): boolean =>
  samples.some((s) => Boolean(s.youtubeId));

/** schema.org VideoObject JSON-LD for each published sample (rendered on /videos). */
export const sampleVideoJsonLd = (samples: VideoSample[] = VIDEO_SAMPLES) =>
  samples
    .filter((s) => s.youtubeId)
    .map((s) => ({
      '@context': 'https://schema.org',
      '@type': 'VideoObject',
      name: s.title,
      description: s.description,
      thumbnailUrl: s.poster ? `https://axai.com.au${s.poster}` : `https://i.ytimg.com/vi/${s.youtubeId}/hqdefault.jpg`,
      ...(s.uploadDate ? { uploadDate: s.uploadDate } : {}),
      embedUrl: `https://www.youtube-nocookie.com/embed/${s.youtubeId}`,
      contentUrl: `https://www.youtube.com/watch?v=${s.youtubeId}`,
      ...(s.transcript ? { transcript: s.transcript.join('\n\n') } : {}),
    }));

export const VIDEO_BENEFITS = [
  { title: 'A presenter on call for every video', text: 'Your AI avatar presenter delivers every video in the same warm, consistent voice. Ready whenever you need a new video, on your timeline.' },
  { title: 'Looks and sounds like you', text: 'Your logo, colours and fonts on every scene, with title and closing cards to match your brand.' },
  { title: 'Easy to keep up to date', text: 'When a policy or service changes, we update the video. Updates are quick: we change only the part that changed.' },
  { title: 'Accessible from the start', text: 'Captions on every video, clear pacing and plain language. Easy Read and other languages on request.' },
  { title: 'Fixed, affordable prices', text: 'You know the cost before we start: fixed prices, set-up included.' },
  { title: 'Made by a team you can meet', text: `Your script, scenes and captions are prepared by the same people from start to finish, and you can talk to them at every step. [Read how we work](${AI_SUPPORTED_WORK_PATH}).` },
];

export const VIDEO_PRESENTERS = [
  { title: 'Your own presenter', text: 'An AI avatar of someone on your team, made with their written permission, so your videos have a familiar face.' },
  { title: 'One of our presenters', text: 'Choose from a range of professional AI avatar presenters and voices, including Australian accents.' },
  { title: 'A character for your brand', text: 'A custom presenter designed to suit your organisation and audience.' },
];

export const VIDEO_SUITES = [
  { name: 'Staff induction', pieces: 'Welcome new staff, explain your values and walk them through what they need to know from day one.' },
  { name: 'Staff training', pieces: 'Turn policies and procedures into short videos people actually watch and remember.' },
  { name: 'Participant and client information', pieces: 'Explain your services, rights and how to get help in a clear, friendly way.' },
  { name: 'Service explainers', pieces: 'Show who you help, how it works and how to get started, for your website and social media.' },
  { name: 'Recruitment', pieces: 'Show what the job is really like and what a day at your organisation looks like.' },
  { name: 'Small business launches', pieces: 'Introduce your business and products with a set of short, shareable videos.' },
];

export const VIDEO_STEPS = [
  { title: 'Tell us what you need', text: 'Send your document, web page or notes, and your logo.' },
  { title: 'Approve the script', text: 'We write it with you, or use your own.' },
  { title: 'Review your video', text: 'Watch it and ask for any changes.' },
  { title: 'Use it anywhere', text: 'Download your videos with captions, ready to share.' },
];
